import { OktaAuth, IDToken, AccessToken, UserClaims } from '@okta/okta-auth-js';

export function getDatabricksJobIds(): Record<string, Record<string, string>> {
  let mappingStr: string | undefined;
  if (process.env.NODE_ENV === 'development') {
    mappingStr = process.env.VITE_DATABRICKS_JOB_ID_NAMES;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    mappingStr = '{"512234983794183":{"name":"Factiva Update","description":"Fetch articles from Dow Jones"},"848055007147763":{"name":"Newstrack Pipeline", "description":"Process articles and calculate fss score"},"173752563429173":{"name":"External Feeds","description":"Update delta share tables used by Dow Jones"},"100722232983921":{"name":"Data Checks","description":"Data quality checks"}}';
  }

  try {
    return mappingStr ? JSON.parse(mappingStr) : {};
  } catch {
    return {};
  }
}

export function getDjAuthnDomain() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DJ_AUTHN_DOMAIN;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'accounts.dowjones.com';
  }
}

export function getDjClientId() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DJ_CLIENT_ID;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'jNT2Yi2NXlDA3qwvWe9hZLqy0278Qw8u0jSWy9sx';
  }
}

export function getDjAuthzDomain() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DJ_AUTHZ_DOMAIN;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'api.dowjones.com';
  }
}

export function getOktaDomain() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_OKTA_DOMAIN;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'owg.okta.com';
  }
}

export function getOktaClientId() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_OKTA_CLIENT_ID;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return '0oaixi799wA00CifQ2p7';
  }
}

export function getOktaAuthServerId() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_OKTA_AUTH_SERVER_ID;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'ausixicb5eILCbXEG2p7';
  }
}

export function getDatadogApplicationId() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DATADOG_APPLICATION_ID;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return '7f80635c-bbaa-4340-90c4-875174316d57';
  }
}

export function getDatadogClientToken() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DATADOG_CLIENT_TOKEN;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'pub422a231cf92ec02c659a51bad67d7c15';
  }
}

export function getDatadogSite() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DATADOG_SITE;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'datadoghq.com';
  }
}

export function getDatadogService() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DATADOG_SERVICE;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'factiva-sentiment-signals';
  }
}

export function getDatadogEnv() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_DATADOG_ENV;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'prod';
  }
}

export function getApiUrl() {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_API_URL;
  } else {
    /*eslint no-template-curly-in-string: "off"*/
    return 'https://fss-api.owex.oliverwyman.com/api/';
  }
}

export function getFeatureFlags(): Set<string> {
  /*eslint no-template-curly-in-string: "off"*/
  const flags = process.env.NODE_ENV === 'development' ? (process.env.VITE_FEATURE_FLAGS ?? '') : 'bulk_loader,new_search,download_chart';
  const split_flags = flags
    .split(',')
    .filter((x) => x)
    .map((x) => x.trim());
  return new Set(split_flags);
}

// https://github.com/facebook/create-react-app/issues/12212
// https://create-react-app.dev/docs/adding-custom-environment-variables/

export function isAuthEnabled(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_AUTH_ENABLED === 'true';
  } else if (process.env.NODE_ENV === 'test') {
    return true;
  } else {
    /* eslint no-template-curly-in-string: "off" */
    const authEnabledFlag = 'true';
    // @ts-expect-error replaced at deploy time
    return authEnabledFlag !== 'false';
  }
}

export function isExternalRoutes(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return process.env.VITE_USE_EXTERNAL_ROUTES === 'true';
  } else {
    /* eslint no-template-curly-in-string: "off" */
    const useExternalRoutesFlag = 'true';
    // @ts-expect-error replaced at deploy time
    return useExternalRoutesFlag !== 'false';
  }
}

export async function digestMessage(message: string | undefined) {
  if (message === undefined) {
    return '';
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const dataView = new DataView(hash);
  return dataView.getUint32(0).toString();
}

export async function GetOktaUserClaims(oktaAuth: OktaAuth): Promise<UserClaims | undefined> {
  if (!oktaAuth) return undefined;
  const tokenManager = oktaAuth.tokenManager;
  const accessToken: AccessToken = (await tokenManager.get('accessToken')) as AccessToken;
  const idToken: IDToken = (await tokenManager.get('idToken')) as IDToken;
  if (!idToken) return undefined;
  const userInfo: UserClaims = await oktaAuth.token.getUserInfo(accessToken, idToken);
  return userInfo;
}
